import { Stage, Layer, Rect, Text, Group } from 'react-konva';
import React, { useMemo } from 'react';
import { Container, FormLabel, Row, Col } from 'react-bootstrap';

/* ===========================================================================
 *  TOP VIEW GEOMETRY
 * ---------------------------------------------------------------------------
 *  Walls are stored as a CHAIN: walls[0] is the left most wall of the
 *  elevation, walls[walls.length-1] the right most one. Every wall keeps
 *
 *     walldirection : the RELATIVE turn against its neighbour that is closer
 *                     to the reference wall ("zero" wall):
 *                        +90  -> "Towards You"
 *                        -90  -> "Away From You"
 *                          0  -> the reference wall itself
 *     wallwidth     : its length in inches (the elevation width)
 *
 *  From that we derive, for every wall, an ABSOLUTE angle and the two points
 *  of its room-side face. The walls are then a simple poly-line:
 *
 *      end(i) === start(i+1)                 (walls share their corner)
 *      angle(i) = angle(i-1) + dir(i)        (to the right of the zero wall)
 *      angle(i) = angle(i+1) - dir(i)        (to the left  of the zero wall)
 *
 *  The old implementation tried to do this with a table of hard coded
 *  switch/case offsets. It had no case for accumulated angles of +-270 (which
 *  happen as soon as a design has three turns in the same direction) and it
 *  mixed the relative and the absolute angle, so from the 4th wall on the
 *  walls were drawn off by the wall thickness, on top of each other or
 *  rotated in the wrong direction. Everything below is plain vector maths and
 *  works for any number of walls and any combination of side/direction.
 * ======================================================================== */

export const TOPVIEW_SCALE = 2;             // pixels per inch in the top view
export const TOPVIEW_WALL_THICKNESS = 6;    // drawn thickness of a wall (px)

const STAGE_WIDTH = 500;
const STAGE_HEIGHT = 280;
const STAGE_PADDING = 16;

const topviewinchtopoint = (inch) => (parseFloat(inch) || 0) * TOPVIEW_SCALE;

// angle helpers. Angles are degrees, clockwise, 0 = pointing to the right.
// (Konva/canvas y axis points down, so +90 points "down" = towards the viewer)
export const normalizeAngle = (deg) => {
  const r = ((deg % 360) + 360) % 360;
  return r > 180 ? r - 360 : r;
};

// every angle of a kitchen layout is a multiple of 90, so rounding the
// cos/sin keeps the arithmetic exact (no floating point drift).
const headingOf = (deg) => ({
  x: Math.round(Math.cos((deg * Math.PI) / 180)),
  y: Math.round(Math.sin((deg * Math.PI) / 180)),
});

// 90 degrees clockwise: this is the side of the wall where the room (and the
// cabinets, and the user looking at the elevation) is.
const rot90cw = (v) => ({ x: -v.y, y: v.x });

const directionOf = (wall) => {
  const d = parseInt(wall && wall.walldirection, 10);
  return Number.isNaN(d) ? 0 : d;
};

/** index of the reference wall; falls back to the wall with direction 0 */
export const resolveZeroIndex = (walls, zeroWallIndex) => {
  if (!walls || walls.length === 0) return 0;
  const idx = parseInt(zeroWallIndex, 10);
  if (!Number.isNaN(idx) && idx >= 0 && idx < walls.length) return idx;
  const byDirection = walls.findIndex((w) => directionOf(w) === 0);
  return byDirection >= 0 ? byDirection : 0;
};

/** absolute angle of every wall of the chain */
export const computeWallAngles = (walls, zeroWallIndex) => {
  if (!walls || walls.length === 0) return [];
  const zero = resolveZeroIndex(walls, zeroWallIndex);
  const angles = new Array(walls.length).fill(0);
  angles[zero] = 0;
  for (let i = zero + 1; i < walls.length; i++) {
    angles[i] = normalizeAngle(angles[i - 1] + directionOf(walls[i]));
  }
  for (let i = zero - 1; i >= 0; i--) {
    // a wall inserted to the LEFT is walked in the opposite sense, so its
    // relative turn has to be subtracted instead of added.
    angles[i] = normalizeAngle(angles[i + 1] - directionOf(walls[i]));
  }
  return angles;
};

/**
 * Places every wall of the chain.
 * @returns {{walls:Array, bounds:Object, zeroIndex:number}}
 *  every entry: { index, id, name, angle, heading, length, thickness,
 *                 start, end, box, rectProps }
 *  - start/end are the ends of the room side face of the wall,
 *  - box is the axis aligned bounding box of the drawn wall,
 *  - rectProps can be spread straight into a Konva <Rect/>.
 */
export const computeWallLayout = (walls, zeroWallIndex, origin = { x: 0, y: 0 }) => {
  if (!walls || walls.length === 0) {
    return { walls: [], bounds: { minX: 0, minY: 0, maxX: 0, maxY: 0 }, zeroIndex: 0 };
  }

  const zero = resolveZeroIndex(walls, zeroWallIndex);
  const angles = computeWallAngles(walls, zero);
  const lengths = walls.map((w) => topviewinchtopoint(w.wallwidth));
  const t = TOPVIEW_WALL_THICKNESS;

  const start = new Array(walls.length);
  const end = new Array(walls.length);

  // the reference wall starts at the origin and goes to the right
  start[zero] = { ...origin };
  end[zero] = {
    x: origin.x + lengths[zero] * headingOf(angles[zero]).x,
    y: origin.y + lengths[zero] * headingOf(angles[zero]).y,
  };

  // ... everything on its right continues from the previous end point
  for (let i = zero + 1; i < walls.length; i++) {
    const h = headingOf(angles[i]);
    start[i] = { ...end[i - 1] };
    end[i] = { x: start[i].x + lengths[i] * h.x, y: start[i].y + lengths[i] * h.y };
  }
  // ... and everything on its left finishes where the next wall starts
  for (let i = zero - 1; i >= 0; i--) {
    const h = headingOf(angles[i]);
    end[i] = { ...start[i + 1] };
    start[i] = { x: end[i].x - lengths[i] * h.x, y: end[i].y - lengths[i] * h.y };
  }

  const placed = walls.map((wall, i) => {
    const h = headingOf(angles[i]);
    const n = rot90cw(h); // points to the room side, the wall body is drawn on the other side
    const corners = [
      start[i],
      end[i],
      { x: start[i].x - t * n.x, y: start[i].y - t * n.y },
      { x: end[i].x - t * n.x, y: end[i].y - t * n.y },
    ];
    return {
      index: i,
      id: wall.id,
      name: wall.wallname,
      angle: angles[i],
      heading: h,
      roomNormal: n,
      length: lengths[i],
      thickness: t,
      start: start[i],
      end: end[i],
      box: {
        minX: Math.min(...corners.map((c) => c.x)),
        maxX: Math.max(...corners.map((c) => c.x)),
        minY: Math.min(...corners.map((c) => c.y)),
        maxY: Math.max(...corners.map((c) => c.y)),
      },
      // Konva props. The offset puts the anchor on the face line, so the body
      // of the wall is always drawn on the non-room side, whatever the angle.
      rectProps: {
        x: start[i].x,
        y: start[i].y,
        width: lengths[i],
        height: t,
        rotation: angles[i],
        offset: { x: 0, y: t },
      },
      rects: wall.rectangles,
      wallrectangles: wall.wallrectangles,
    };
  });

  const bounds = placed.reduce(
    (acc, w) => ({
      minX: Math.min(acc.minX, w.box.minX),
      maxX: Math.max(acc.maxX, w.box.maxX),
      minY: Math.min(acc.minY, w.box.minY),
      maxY: Math.max(acc.maxY, w.box.maxY),
    }),
    { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity }
  );

  return { walls: placed, bounds, zeroIndex: zero };
};

/* ===========================================================================
 *  COLLISIONS
 * ---------------------------------------------------------------------------
 *  A layout is valid when no two walls share more than the little square where
 *  they meet. Closed rooms are therefore allowed (the last wall may end on the
 *  first one), while walls crossing each other or lying on top of each other
 *  are not.
 * ======================================================================== */

const EPS = 1e-6;
const isHorizontal = (w) => Math.abs(w.start.y - w.end.y) < EPS;
const strictlyInside = (v, a, b) => v > Math.min(a, b) + EPS && v < Math.max(a, b) - EPS;
const inRange = (v, a, b) => v >= Math.min(a, b) - EPS && v <= Math.max(a, b) + EPS;

const overlapArea = (a, b) =>
  Math.max(0, Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX)) *
  Math.max(0, Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY));

/** 'none' | 'touch' (single point) | 'cross' | 'collinear' */
export const faceRelation = (w1, w2) => {
  const h1 = isHorizontal(w1);
  const h2 = isHorizontal(w2);

  if (h1 === h2) {
    const fixed1 = h1 ? w1.start.y : w1.start.x;
    const fixed2 = h2 ? w2.start.y : w2.start.x;
    if (Math.abs(fixed1 - fixed2) > EPS) return 'none';
    const a = h1 ? [w1.start.x, w1.end.x] : [w1.start.y, w1.end.y];
    const b = h2 ? [w2.start.x, w2.end.x] : [w2.start.y, w2.end.y];
    const lo = Math.max(Math.min(...a), Math.min(...b));
    const hi = Math.min(Math.max(...a), Math.max(...b));
    if (hi - lo > EPS) return 'collinear';
    if (hi - lo > -EPS) return 'touch';
    return 'none';
  }

  const hor = h1 ? w1 : w2;
  const ver = h1 ? w2 : w1;
  const y = hor.start.y;
  const x = ver.start.x;
  if (!inRange(x, hor.start.x, hor.end.x) || !inRange(y, ver.start.y, ver.end.y)) return 'none';
  if (strictlyInside(x, hor.start.x, hor.end.x) && strictlyInside(y, ver.start.y, ver.end.y)) {
    return 'cross';
  }
  return 'touch';
};

/**
 * @returns [{i, j, type}] - empty when the layout is valid.
 *   type: 'cross'     -> the walls cross each other
 *         'collinear' -> the walls lie on top of each other
 *         'overlap'   -> the bodies of the walls overlap
 */
export const findWallCollisions = (walls, zeroWallIndex) => {
  const { walls: placed } = computeWallLayout(walls, zeroWallIndex);
  const found = [];
  for (let i = 0; i < placed.length; i++) {
    for (let j = i + 1; j < placed.length; j++) {
      const relation = faceRelation(placed[i], placed[j]);
      if (relation === 'cross' || relation === 'collinear') {
        found.push({ i, j, type: relation });
        continue;
      }
      // neighbours always share the corner square; anybody else must not
      // overlap more than that.
      const maxAllowed = TOPVIEW_WALL_THICKNESS * TOPVIEW_WALL_THICKNESS + EPS;
      if (j !== i + 1 && overlapArea(placed[i].box, placed[j].box) > maxAllowed) {
        found.push({ i, j, type: 'overlap' });
      }
    }
  }
  return found;
};

export const wallsCollide = (walls, zeroWallIndex) =>
  findWallCollisions(walls, zeroWallIndex).length > 0;

/** human readable message for the collisions of a layout */
export const describeWallCollisions = (walls, zeroWallIndex, collisions) => {
  const list = collisions || findWallCollisions(walls, zeroWallIndex);
  if (list.length === 0) return '';
  const nameOf = (i) => (walls[i] && walls[i].wallname ? walls[i].wallname : `wall ${i + 1}`) + ` (#${i + 1})`;
  const { i, j, type } = list[0];
  const what =
    type === 'collinear'
      ? 'would lie on top of'
      : type === 'cross'
      ? 'would cross'
      : 'would overlap';
  return (
    `This wall cannot be inserted here: ${nameOf(j)} ${what} ${nameOf(i)}. ` +
    'Walls are not allowed to collide - change the side, the direction or the width of the wall. ' +
    '(Closing the room is allowed, walls crossing each other is not.)'
  );
};

/**
 * Legacy helper kept for backwards compatibility: same shape the previous
 * implementation returned ({placedRects}), but now computed with the geometry
 * above. `currentangle` is the ABSOLUTE angle of the wall and
 * `previousangleSum` is kept at 0 so that `currentangle + previousangleSum`
 * still gives the rotation of the wall.
 */
export const computeBoundingBox = (walls, zeroWallIndex) => {
  const { walls: placed } = computeWallLayout(walls, zeroWallIndex, { x: 20, y: 86 });
  return {
    placedRects: placed.map((w) => ({
      x: w.rectProps.x,
      y: w.rectProps.y,
      currentwidth: w.length,
      currentheight: w.thickness,
      currentangle: w.angle,
      previousangleSum: 0,
      id: w.id,
      rects: w.rects,
    })),
  };
};

/* ===========================================================================
 *  TOP VIEW
 * ======================================================================== */

export const RectanglesVisualizer = (props) => {
  const walls = props.walls || [];
  const currentWallIndex = parseInt(props.currentWallIndex, 10) || 0;

  const layout = useMemo(
    () => computeWallLayout(walls, props.zeroIndex),
    [walls, props.zeroIndex]
  );

  // fit the whole layout inside the stage (it used to be drawn at a fixed
  // position, so with a few walls half of the design ended up outside of the
  // visible area). The stage is still draggable.
  const view = useMemo(() => {
    const { bounds } = layout;
    if (!layout.walls.length || !isFinite(bounds.minX)) return { scale: 1, dx: 0, dy: 0 };
    const w = Math.max(bounds.maxX - bounds.minX, 1);
    const h = Math.max(bounds.maxY - bounds.minY, 1);
    const scale = Math.min(1, (STAGE_WIDTH - 2 * STAGE_PADDING) / w, (STAGE_HEIGHT - 2 * STAGE_PADDING) / h);
    return {
      scale,
      dx: (STAGE_WIDTH - w * scale) / 2 - bounds.minX * scale,
      dy: (STAGE_HEIGHT - h * scale) / 2 - bounds.minY * scale,
    };
  }, [layout]);

  return (
    <>
      <Container className='mt-2' style={{borderStyle:'solid',borderWidth:'1px', margin:0, maxWidth:"500px"}}>
        <Col style={{maxWidth:"500px"}}>
          <Row style={{display:'flex',flexDirection:'row', flexWrap:'nowrap', height:'20px'}}>
            <FormLabel>Top View (You can drag the whole view)</FormLabel>
          </Row>
          <Row style={{backgroundColor:'lightgray', height:'100%', maxWidth:"500px"}}>
            <Stage
              width={STAGE_WIDTH}
              height={STAGE_HEIGHT}
              draggable
              onMouseMove={(e) => { e.target.getStage().container().style.cursor = 'move'; }}
              onMouseLeave={(e) => { e.target.getStage().container().style.cursor = 'default'; }}
            >
              <Layer>
                <Group x={view.dx} y={view.dy} scaleX={view.scale} scaleY={view.scale}>
                  {layout.walls.map((wall) => {
                    const isCurrent = wall.index === currentWallIndex;
                    return (
                      <Rect
                        key={wall.id || wall.index}
                        {...wall.rectProps}
                        fill={isCurrent ? 'blue' : 'gray'}
                        stroke={isCurrent ? 'blue' : 'black'}
                        strokeWidth={0}
                      />
                    );
                  })}
                  {/* number of every wall, so the order of the chain is visible */}
                  {layout.walls.map((wall) => {
                    const label = String(wall.index + 1);
                    const mid = {
                      x: (wall.start.x + wall.end.x) / 2 - wall.roomNormal.x * 9,
                      y: (wall.start.y + wall.end.y) / 2 - wall.roomNormal.y * 9,
                    };
                    return (
                      <Text
                        key={`label-${wall.id || wall.index}`}
                        text={label}
                        x={mid.x - 6}
                        y={mid.y - 6}
                        width={12}
                        height={12}
                        align='center'
                        verticalAlign='middle'
                        fontSize={11 / view.scale}
                        scaleX={1 / view.scale}
                        scaleY={1 / view.scale}
                        fill={wall.index === currentWallIndex ? 'blue' : '#333'}
                      />
                    );
                  })}
                </Group>
              </Layer>
            </Stage>
          </Row>
        </Col>
      </Container>
    </>
  );
};